<template>
  <b-modal
    v-model="isOpen"
    hide-header
    hide-footer
    centered
    modal-class="vendor-outcome-modal"
  >
    <div class="outcome-wrap text-center">
      <button
        type="button"
        class="outcome-close"
        aria-label="Close"
        @click="close"
      >
        <feather-icon
          icon="XIcon"
          size="18"
        />
      </button>

      <div
        class="outcome-icon"
        :class="`outcome-icon--${variant}`"
      >
        <feather-icon
          :icon="iconName"
          size="42"
        />
      </div>

      <h3 class="outcome-title">
        {{ resolvedTitle }}
      </h3>
      <p class="outcome-message">
        {{ resolvedMessage }}
      </p>

      <b-button
        variant="primary"
        class="outcome-btn"
        block
        @click="onPrimary"
      >
        {{ resolvedPrimaryLabel }}
      </b-button>
      <b-button
        v-if="resolvedSecondaryLabel"
        variant="outline-secondary"
        class="outcome-btn-secondary mt-1"
        block
        @click="onSecondary"
      >
        {{ resolvedSecondaryLabel }}
      </b-button>
    </div>
  </b-modal>
</template>

<script>
import { BModal, BButton } from 'bootstrap-vue'

const PRESETS = {
  'ad-submitted': {
    icon: 'SendIcon',
    title: 'Ad Submitted',
    message: 'Your ad has been submitted for review. We will notify you once it is approved.',
    primaryLabel: 'Go to My Ads',
  },
  'ad-approved': {
    icon: 'CheckCircleIcon',
    title: 'Ad Approved',
    message: 'Your ad is now live and running.',
    primaryLabel: 'View Ad',
  },
  'ad-rejected': {
    icon: 'XCircleIcon',
    title: 'Ad Rejected',
    message: 'Your ad was not approved. Please review the guidelines and try again.',
    primaryLabel: 'Edit Ad',
  },
  'plan-upgraded': {
    icon: 'AwardIcon',
    title: 'Plan Upgraded',
    message: 'Your subscription has been upgraded successfully.',
    primaryLabel: 'View Billing',
  },
  'company-registered': {
    icon: 'CheckCircleIcon',
    title: 'Company Registered Successfully',
    message: 'Your company account has been created. You can now sign in and start managing ads.',
    primaryLabel: 'Go to Sign In',
  },
  suspended: {
    icon: 'SlashIcon',
    title: 'Account Suspended',
    message: 'Your advertiser account is suspended. Contact support for assistance.',
    primaryLabel: 'Contact Help',
    secondaryLabel: 'Back to Login',
  },
}

export default {
  components: {
    BModal,
    BButton,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    variant: {
      type: String,
      default: 'ad-submitted',
      validator: v => ['ad-submitted', 'ad-approved', 'ad-rejected', 'plan-upgraded', 'company-registered', 'suspended'].includes(v),
    },
    title: {
      type: String,
      default: '',
    },
    message: {
      type: String,
      default: '',
    },
    primaryLabel: {
      type: String,
      default: '',
    },
    secondaryLabel: {
      type: String,
      default: '',
    },
  },
  computed: {
    isOpen: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      },
    },
    preset() {
      return PRESETS[this.variant] || PRESETS['ad-submitted']
    },
    iconName() {
      return this.preset.icon
    },
    resolvedTitle() {
      return this.title || this.preset.title
    },
    resolvedMessage() {
      return this.message || this.preset.message
    },
    resolvedPrimaryLabel() {
      return this.primaryLabel || this.preset.primaryLabel
    },
    resolvedSecondaryLabel() {
      return this.secondaryLabel || this.preset.secondaryLabel || ''
    },
  },
  methods: {
    close() {
      this.$emit('update:visible', false)
      this.$emit('close')
    },
    onPrimary() {
      this.$emit('primary', this.variant)
      this.close()
    },
    onSecondary() {
      this.$emit('secondary', this.variant)
      this.close()
    },
  },
}
</script>

<style scoped lang="scss">
.vendor-outcome-modal {
  .modal-content {
    border-radius: 12px;
    border: none;
  }
}

.outcome-wrap {
  position: relative;
  padding: 1.5rem 1.25rem 1.25rem;
}

.outcome-close {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  border: none;
  background: transparent;
  color: #6e6b7b;
  padding: 0.25rem;
}

.outcome-icon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;

  &--ad-submitted {
    background: rgba(115, 103, 240, 0.12);
    color: #7367f0;
  }

  &--ad-approved {
    background: rgba(40, 199, 111, 0.12);
    color: #28c76f;
  }

  &--ad-rejected {
    background: rgba(234, 84, 85, 0.12);
    color: #ea5455;
  }

  &--plan-upgraded {
    background: rgba(0, 207, 232, 0.12);
    color: #00cfe8;
  }

  &--company-registered {
    background: rgba(40, 199, 111, 0.12);
    color: #28c76f;
  }

  &--suspended {
    background: rgba(255, 159, 67, 0.15);
    color: #ff9f43;
  }
}

.outcome-title {
  color: #4b465c;
  font-size: 1.35rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.outcome-message {
  color: #6e6b7b;
  font-size: 0.95rem;
  margin-bottom: 1.25rem;
}

.outcome-btn {
  background: #041122 !important;
  border-color: #041122 !important;
  border-radius: 8px !important;
}

.outcome-btn-secondary {
  border-color: #dbdade;
  color: #6e6b7b;
}
</style>
